import {
  Box,
  TableCell,
  TableRow,
  Typography,
  TableContainer,
  TableHead,
  TableBody,
  Paper,
  Table,
  Snackbar,
  Alert,
  TablePagination,
  Button,
  IconButton,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { AddProvider } from "./AddProvider";
import { IconButtonComponent } from "../../../common/IconButtonComponent";
import { Add } from "@mui/icons-material";
import { getProvidersPagination } from "../helpers/getProvidersPagination";
import { MergeSupplieProvider } from "./MergeSupplieProvider";
import { EditProvider } from "./EditProvider";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { deleteProvider } from "../../../../utils";
import { ConfirmDialog } from "../../../common/ConfirmDialog";

export const TablaProviders = () => {
  const token = sessionStorage.getItem("token");

  const [providers, setProviders] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pageChild, setPageChild] = useState(0);
  const [pageSizeChild, setPageSizeChild] = useState(8);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);


  const [reloadTick, setReloadTick] = useState(0);
  const reload = () => setReloadTick((t) => t + 1);

  const fetchProviders = useCallback(async () => {
    try {
      const res = await getProvidersPagination({
        token,
        page: pageChild + 1,
        pageSize: pageSizeChild,
      });
      setProviders(res.data.items || []);
      setTotalItems(parseInt(res.data.totalItems ?? 0, 10));
    } catch (error) {
      console.error(error);
    }
  }, [token, pageChild, pageSizeChild]);


  const handleDeleteClick = (provider) => {
    setProviderToDelete(provider);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    await deleteProvider(token, providerToDelete.id);
    setSnackbarMessage(`Provider "${providerToDelete.name}" deleted!`);
    setOpenSnackbar(true);
    reload();
  };

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders, reloadTick]);

  const Row = ({ data }) => (
    <TableRow sx={{ "& > *": { borderBottom: "unset" }, wordBreak: "break-word" }} hover>
      <TableCell>{data.name}</TableCell>
      <TableCell>{data.address}</TableCell>

      <TableCell>
        {/* Manage Supplies */}
        <BaseModalComponent
          modalKey={`Manage-supplies-${data.id}`}
          modalLabel={`Manage Supplies - ${data.name}`}
          elementTrigger={
            <Button
              variant="outlined"
              startIcon={<Inventory2OutlinedIcon />}
              size="small"
              sx={{ fontSize: "0.8rem", textTransform: "none" }}
            >
              Manage Supplies
            </Button>
          }
        >
          <MergeSupplieProvider
            provider={data}
            close={() => { }}
          />
        </BaseModalComponent>
      </TableCell>

      <TableCell sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {/* Edit */}
        <BaseModalComponent
          modalKey={`edit-provider-${data.id}`}
          modalLabel="Edit Provider"
          elementTrigger={
            <IconButton size="small" color="primary" aria-label="edit">
              <EditIcon />
            </IconButton>
          }
        >
          <EditProvider
            modalKey={`edit-provider-${data.id}`}
            provider={data}
            onSuccess={() => {
              setSnackbarMessage("Provider updated successfully!");
              setOpenSnackbar(true);
              reload();
            }}
            onClose={() => { }}
          />
        </BaseModalComponent>

        {/* Delete */}
        <IconButton
          size="small"
          color="error"
          aria-label="delete"
          onClick={() => handleDeleteClick(data)}
        >
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <Box sx={{ width: "100%", overflowX: "auto", mt: 1.5 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Typography variant="h4" sx={{ mb: 2, ml: 2, textAlign: "left" }}>
            Providers
          </Typography>

          {/* Add Provider */}
          <BaseModalComponent
            modalKey="add-provider"
            modalLabel="Add Provider"
            elementTrigger={
              <IconButtonComponent
                sx={{
                  color: "#fff",
                  backgroundColor: "rgb(35, 48, 68)",
                }}
              >
                <Add sx={{ height: 32, width: 42 }} />
              </IconButtonComponent>
            }
          >
            <AddProvider
              modalKey="add-provider"
              onSuccess={() => {
                setSnackbarMessage("Provider added successfully!");
                setOpenSnackbar(true);
                reload();
              }}
              onClose={() => { }}
            />
          </BaseModalComponent>
        </Box>

        <TableContainer component={Paper} sx={{ width: "100%", overflowX: "auto" }}>
          <Table size="medium">
            <TableHead>
              <TableRow sx={{ background: "#d9d9d9" }}>
                <TableCell sx={{ minWidth: 150 }}>Provider</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Address</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Supplies</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {providers?.map((data) => (
                <Row key={data.id} data={data} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[3, 5, 10]}
          component="div"
          count={totalItems}
          rowsPerPage={pageSizeChild}
          page={pageChild}
          onPageChange={(e, newPage) => setPageChild(newPage)}
          onRowsPerPageChange={(e) => {
            setPageSizeChild(parseInt(e.target.value, 10));
            setPageChild(0);
          }}
        />
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete Provider"
        description={`Are you sure you want to delete provider "${providerToDelete?.name}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
